import React, { createContext, useContext, useCallback, useState, ReactNode } from 'react';

export interface AppError {
    id: string;
    message: string;
    type: 'relay' | 'network' | 'general' | 'validation';
    timestamp: Date;
    details?: any;
}

interface ErrorContextType {
    errors: AppError[];
    addError: (error: Omit<AppError, 'id' | 'timestamp'>) => void;
    removeError: (id: string) => void;
    clearAllErrors: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useErrorContext = () => {
    const context = useContext(ErrorContext);
    if (!context) {
        throw new Error('useErrorContext must be used within an ErrorProvider');
    }
    return context;
};

interface ErrorProviderProps {
    children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
    const [errors, setErrors] = useState<AppError[]>([]);

    const addError = useCallback((error: Omit<AppError, 'id' | 'timestamp'>) => {
        const newError: AppError = {
            ...error,
            id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date(),
        };
        setErrors(prev => [...prev, newError]);
    }, []);

    const removeError = useCallback((id: string) => {
        setErrors(prev => prev.filter(error => error.id !== id));
    }, []);

    const clearAllErrors = useCallback(() => {
        setErrors([]);
    }, []);

    return (
        <ErrorContext.Provider value={{ errors, addError, removeError, clearAllErrors }}>
            {children}
        </ErrorContext.Provider>
    );
};
